<script setup>
import { computed, onMounted, watch } from "vue";
import { ChevronLeft, ChevronRight, RefreshCw, XCircle } from "lucide-vue-next";
import AppHeader from "@/components/layout/AppHeader.vue";
import AppFooter from "@/components/layout/AppFooter.vue";
import LoginPanel from "@/components/common/LoginPanel.vue";
import { leaderboardModeOptions, providerOptions } from "@/shared/config/constants";
import { compact, providerLabel, utc8 } from "@/shared/utils/format";
import { useAuthStore } from "@/stores/auth";
import { useLeaderboardStore } from "@/stores/leaderboard";
import { useReleaseStore } from "@/stores/release";
import GoalBoard from "@/features/leaderboard/components/GoalBoard.vue";
import PodiumSection from "@/features/leaderboard/components/PodiumSection.vue";
import StatCard from "@/features/leaderboard/components/StatCard.vue";
import LeaderboardTable from "@/features/leaderboard/components/LeaderboardTable.vue";
import DownloadPanel from "@/features/leaderboard/components/DownloadPanel.vue";
import TokenHeatmap from "@/features/leaderboard/components/TokenHeatmap.vue";

const auth = useAuthStore();
const leaderboard = useLeaderboardStore();
const release = useReleaseStore();

const selectedProviderLabel = computed(() => providerLabel(leaderboard.provider));
const metrics = computed(() => leaderboard.metrics);
const isTotalMode = computed(() => leaderboard.mode === "total");
const isDayMode = computed(() => leaderboard.mode === "day");
const isAggregateMode = computed(() => !isDayMode.value);
const rankedItems = computed(() => leaderboard.items.filter((item) => item.totalTokens > 0));
const zeroItems = computed(() => leaderboard.items.filter((item) => item.totalTokens === 0));
const showPodium = computed(
  () => !isTotalMode.value && leaderboard.pageInfo.offset === 0 && rankedItems.value.length >= 3
);
const podiumItems = computed(() => rankedItems.value.slice(0, 3));
const listItems = computed(() => (showPodium.value ? [...rankedItems.value.slice(3), ...zeroItems.value] : leaderboard.items));
const pageStart = computed(() => (leaderboard.items.length ? leaderboard.pageInfo.offset + 1 : 0));
const pageEnd = computed(() => leaderboard.pageInfo.offset + leaderboard.items.length);
const modeLabel = computed(() => leaderboardModeOptions.find((option) => option.value === leaderboard.mode)?.label ?? "");
const currentWindowLabel = computed(() => {
  if (leaderboard.mode === "week") return "本周";
  if (leaderboard.mode === "month") return "本月";
  if (leaderboard.mode === "quarter") return "本季度";
  if (leaderboard.mode === "year") return "本年";
  return "今日";
});
const statusLabel = computed(() => {
  if (isTotalMode.value) return "累计排行";
  return leaderboard.isCurrentWindow ? "进行中" : "已结束";
});
const showGoalBoard = computed(() => ["day", "year", "total"].includes(leaderboard.mode));

const boardTitle = computed(() => {
  if (isTotalMode.value) return leaderboard.provider === "all" ? "已上传总量榜" : `${selectedProviderLabel.value} 总量榜`;
  const base = leaderboard.provider === "all" ? `${modeLabel.value}榜` : `${selectedProviderLabel.value} ${modeLabel.value}榜`;
  return showPodium.value ? `${base} · 第 4 名起` : base;
});

const goalTokens = computed(() =>
  isDayMode.value ? metrics.value.myUsage?.totalTokens ?? 0 : metrics.value.myAggregate?.totalTokens ?? 0
);
const myTokenValue = computed(() => {
  const value = isDayMode.value ? metrics.value.myUsage?.totalTokens : metrics.value.myAggregate?.totalTokens;
  return value ? compact.format(value) : "—";
});
const myRankValue = computed(() => (metrics.value.myRank ? `#${metrics.value.myRank}` : "—"));
const averageTokenValue = computed(() => (metrics.value.averageTokens == null ? "—" : compact.format(metrics.value.averageTokens)));
const averageLabel = computed(() => (isAggregateMode.value ? "平均总量" : "平均 token"));
const myTokenLabel = computed(() => (isAggregateMode.value ? "我的总量" : "我的 token"));
const emptyText = computed(() => {
  if (showPodium.value) return "前三名都在上方领奖台。";
  if (isTotalMode.value) return "还没有已上传总量记录。";
  return isDayMode.value ? "这一天还没有上传记录。" : "这个时间窗口还没有上传记录。";
});

async function setDate(date) {
  leaderboard.setDate(date);
}

onMounted(async () => {
  await auth.loadAuth();
  release.loadMacRelease();
  if (auth.user) leaderboard.loadLeaderboard();
});

watch(
  () => [auth.user?.id, leaderboard.date, leaderboard.provider, leaderboard.mode],
  () => {
    if (auth.user) leaderboard.loadLeaderboard();
  }
);
</script>

<template>
  <main class="app-shell">
    <AppHeader />

    <section class="hero">
      <div class="container">
        <div class="hero-pills">
          <span class="pill-solid">{{ leaderboard.windowLabel }}</span>
          <span class="pill-outline">{{ statusLabel }}</span>
        </div>
        <h1 class="hero-title">token 排行榜</h1>
        <p class="hero-sub">
          用实际行动迎接 agent 范式。<br />
          定个小目标：每天 1 亿 token，2026 年 1000 亿 token。
        </p>

        <div v-if="auth.user" class="hero-controls">
          <div class="segmented" aria-label="榜单模式">
            <button
              v-for="option in leaderboardModeOptions"
              :key="option.value"
              type="button"
              :class="{ active: leaderboard.mode === option.value }"
              @click="leaderboard.setMode(option.value)"
            >
              {{ option.label }}
            </button>
          </div>
          <div class="segmented" aria-label="排行榜类型">
            <button
              v-for="option in providerOptions"
              :key="option.value"
              type="button"
              :class="{ active: leaderboard.provider === option.value }"
              @click="leaderboard.setProvider(option.value)"
            >
              {{ option.label }}
            </button>
          </div>
          <div v-if="!isTotalMode" class="date-controls" aria-label="排行榜日期">
            <button type="button" class="control-btn icon-control" aria-label="上一个时间窗口" @click="leaderboard.moveWindow(-1)">
              <ChevronLeft :size="16" />
            </button>
            <span class="range-label">{{ leaderboard.windowLabel }}</span>
            <button
              type="button"
              class="control-btn icon-control"
              aria-label="下一个时间窗口"
              :disabled="leaderboard.isCurrentWindow || leaderboard.loading"
              @click="leaderboard.moveWindow(1)"
            >
              <ChevronRight :size="16" />
            </button>
            <button
              type="button"
              class="control-btn"
              :class="{ active: leaderboard.isCurrentWindow }"
              @click="leaderboard.moveToCurrentWindow()"
            >
              {{ currentWindowLabel }}
            </button>
          </div>
          <button type="button" class="refresh-btn" aria-label="刷新" @click="leaderboard.refresh()">
            <RefreshCw :size="16" :stroke-width="2.4" :class="{ spin: leaderboard.loading }" />
          </button>
        </div>

        <TokenHeatmap
          v-if="auth.user && isDayMode"
          :year="leaderboard.date.slice(0, 4)"
          :uploads="leaderboard.myYearUploads ?? []"
          :selected-date="leaderboard.date"
          :today="utc8()"
          @select="setDate"
        />
      </div>
    </section>

    <template v-if="auth.loaded && auth.user">
      <GoalBoard
        v-if="showGoalBoard"
        :mode="leaderboard.mode"
        :total-tokens="goalTokens"
        :year-total="isDayMode ? metrics.yearTotal : null"
        :is-today="leaderboard.isToday"
        :date="leaderboard.date"
      />
      <PodiumSection
        v-if="showPodium"
        :items="podiumItems"
        :is-current="leaderboard.isCurrentWindow"
        :current-label="currentWindowLabel"
      />
    </template>

    <section class="board-section">
      <div class="container section-stack">
        <div v-if="!auth.loaded" class="loading-line">正在检查登录态...</div>
        <LoginPanel v-else-if="!auth.user" />
        <template v-else>
          <div v-if="leaderboard.error" class="alert">
            <XCircle :size="16" />
            {{ leaderboard.error }}
          </div>

          <div class="stats-grid">
            <StatCard label="参赛人数" :value="String(metrics.participantCount)" />
            <StatCard :label="averageLabel" :value="averageTokenValue" />
            <StatCard label="我的排名" :value="myRankValue" />
            <StatCard :label="myTokenLabel" :value="myTokenValue" />
          </div>

          <div class="board-card">
            <div class="board-head">
              <div class="board-head-titles">
                <h2 class="board-title">{{ boardTitle }}</h2>
                <span class="board-count">{{ metrics.eligibleCount }} 位成员 · {{ metrics.participantCount }} 位已上传</span>
              </div>
              <a class="membership-source" href="https://infra.candobear.com" target="_blank" rel="noreferrer">
                会员身份由小能熊学习基建提供
              </a>
            </div>
            <LeaderboardTable
              :items="listItems"
              :user="auth.user"
              :date="leaderboard.date"
              :mode="leaderboard.mode"
              :empty-text="emptyText"
            />
            <div v-if="leaderboard.pageInfo.offset > 0 || leaderboard.pageInfo.hasNextPage" class="board-pagination">
              <button
                type="button"
                class="control-btn"
                :disabled="leaderboard.pageInfo.offset === 0 || leaderboard.loading"
                @click="leaderboard.loadPage(leaderboard.pageInfo.offset - leaderboard.pageInfo.limit)"
              >
                上一页
              </button>
              <span>{{ pageStart }}–{{ pageEnd }} / {{ metrics.eligibleCount }}</span>
              <button
                type="button"
                class="control-btn"
                :disabled="!leaderboard.pageInfo.hasNextPage || leaderboard.loading"
                @click="leaderboard.loadPage(leaderboard.pageInfo.offset + leaderboard.pageInfo.limit)"
              >
                下一页
              </button>
            </div>
          </div>
        </template>

        <DownloadPanel :release="release.macRelease" />
      </div>
    </section>

    <AppFooter />
  </main>
</template>
